// Composition root (src/app). Wires platform -> state -> render -> ui as a
// one-way flow: the store is the single source of truth; render and ui subscribe
// read-only (AD-2). Selects the Canvas2D baseline via feature detection and starts
// successfully even when WebGL is unavailable (AD-3).

// Self-hosted fonts, bundled locally by Vite (AD-6): no third-party CDN request.
import '@fontsource/inter/400.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/jetbrains-mono/500.css';
import 'material-symbols/outlined.css';
import '../ui/styles/global.css';

import { mount } from 'svelte';
import type { GraphicsProfile } from '../core/types';
import Workspace from '../ui/Workspace.svelte';
import { createStore } from '../state/store.svelte';
import { createDefaultState, type AppState } from '../state/default-state';
import { detectCapabilities } from '../platform/capabilities';
import { resolveProfile, type RenderCapabilities } from '../render/resolve-profile';
import { startAnimationLoop } from './animation-loop';
import { readPlaybackPref } from '../persist/playback-pref';
import { readGraphicsPrefs } from '../persist/graphics-prefs';
import { decode } from '../share';
import {
  readShareFragment,
  defaultShareableConfig,
  hydrateFromConfig,
  buildShareUrl,
  writeShareFragment
} from './share-state';
import { copyText } from '../platform/clipboard';

// The exact accessible invalid-shared-link notice (EXPERIENCE.md Voice and Tone).
const SHARE_DECODE_FAILURE_NOTICE =
  "That shared link couldn't be read, so the default system loaded.";

const target = document.getElementById('app');
if (!target) {
  throw new Error('Missing #app mount target.');
}

const capabilities = detectCapabilities();

// Test seam (mirrors __KEPLER_AUTORUN__): force WebGL reported unavailable so the
// Enhanced fallback path is deterministic in e2e regardless of the headless
// browser's actual WebGL support. Never affects production (the flag is unset).
const forceNoWebgl =
  typeof window !== 'undefined' &&
  (window as unknown as { __KEPLER_FORCE_NO_WEBGL__?: boolean }).__KEPLER_FORCE_NO_WEBGL__ ===
    true;

// Render-local capability shape (feature detection only, never browser-name).
const renderCaps: RenderCapabilities = { webgl: capabilities.webgl && !forceNoWebgl };

// Story 5.1 (AD-4): the codec/app is the SOLE URL owner. Read + decode + hydrate the
// shared configuration BEFORE the store is constructed and before the first frame,
// so a shared link reconstructs directly (no re-swap, no animated transition). The
// whole read/decode is wrapped so startup can NEVER throw on a hostile fragment
// (AC-11): any failure falls back to the complete default plus the accessible notice.
let initialState: AppState;
let startupNotice = '';
try {
  const fragment = readShareFragment();
  if (fragment === '') {
    // AC-12: empty/absent fragment is a plain cold load, not an error. No notice.
    initialState = createDefaultState();
  } else {
    const result = decode(fragment, defaultShareableConfig());
    if (result.ok) {
      // AC-9/AC-10 clamps + per-field fallbacks already applied inside decode; a
      // successful decode is silent (no notice).
      initialState = hydrateFromConfig(result.config);
    } else {
      // AC-11: unsupported version / undecodable -> full default + the single notice.
      initialState = createDefaultState();
      startupNotice = SHARE_DECODE_FAILURE_NOTICE;
    }
  }
} catch {
  // Belt-and-braces (AC-11): a hostile fragment must never break startup.
  initialState = createDefaultState();
  startupNotice = SHARE_DECODE_FAILURE_NOTICE;
}

const store = createStore(initialState);

// Hydrate the device-local graphics preferences defensively (AD-7): a missing,
// blocked, or invalid store yields Auto + system reduced-motion, and the full
// simulator remains usable (AC 9). These seed the store before mount so the
// canvas host resolves the correct profile on its first build.
const prefs = readGraphicsPrefs();
store.dispatch({ type: 'SetGraphicsProfile', profile: prefs.requestedProfile });
store.dispatch({ type: 'SetReducedEffects', value: prefs.reducedEffects });

// The capability-bound effective-mode resolver passed to the ui (so ui never
// imports platform). CanvasHost re-invokes it to rebuild the decoration overlay on
// a later profile change or a context-loss fallback.
const resolve = (profile: GraphicsProfile) => resolveProfile(profile, renderCaps);

// Record the concrete adapter at startup so the store reflects what is actually
// active from the first frame (Task 9). Startup still succeeds with WebGL
// unavailable, resolving to the Canvas2D baseline (the Story 1.1 guarantee).
const initial = resolve(prefs.requestedProfile);
store.dispatch({
  type: 'SetActiveRenderMode',
  mode: initial.activeMode,
  unavailableFeatures: initial.unavailableFeatures
});

// Story 5.2 (AD-4): the Share button's URL builder. It reuses 5.1's app-layer URL
// owner (`writeShareFragment`) to encode the CURRENT state and write the fragment,
// then returns `location.href` so the copied string and `location.hash` agree exactly
// (no hardcoded domain: it uses the live origin, so the same code works on the
// preview and on the Cloudflare domain). No second encoder/writer is introduced
// (AD-4); the URL/clipboard side effects stay in `app`/`platform` (AD-2), threaded
// into `ui` as callbacks like `resolve`. Writing `location.hash` does not re-run the
// decode (main.ts decodes only at module load; there is no hashchange listener).
const makeShareUrl = (): string => {
  writeShareFragment(store.state);
  return typeof location !== 'undefined' ? location.href : buildShareUrl(store.state);
};

mount(Workspace, {
  target,
  props: {
    store,
    resolve,
    osReducedMotion: capabilities.reducedMotion,
    startupNotice,
    buildShareUrl: makeShareUrl,
    copyText
  }
});

// Story 5.1 e2e seam (mirrors __KEPLER_AUTORUN__ / __KEPLER_FORCE_NO_WEBGL__):
// expose the generated share URL so the e2e can read it without the (Story 5.2)
// Share button. Never affects production behaviour (nothing reads it there).
if (typeof window !== 'undefined') {
  (window as unknown as { __KEPLER_SHARE_URL__?: () => string }).__KEPLER_SHARE_URL__ = () =>
    buildShareUrl(store.state);
}

// Cold-load play/pause decision (Story 1.5). The store starts at the canonical
// default (paused, time 0). Restore the persisted preference, then let the ticker
// obey `paused`:
//   1. A valid first-party cookie wins: 'play' dispatches Play, 'pause' stays paused.
//   2. With no readable cookie, production keeps the product default (paused), but
//      development (`import.meta.env.DEV`) or the `__KEPLER_AUTORUN__` test seam
//      starts UNPAUSED so motion is observable on load. The seam is how Story 1.2's
//      e2e observes motion against the production preview; the "no cookie, no seam,
//      prod -> stays paused" case in that spec must keep holding.
// Simulation time still starts from zero in every case: `createDefaultState` seeds
// `time: 0` and `Play` does not advance time (only the ticker does). This startup
// `Play` is dispatched directly to the store and NEVER writes the cookie; cookie
// writes live only in the HUD's explicit Play/Pause handlers, so Reset and startup
// never mutate the persisted preference (AC 6, 9).
const autoRunSeam =
  typeof window !== 'undefined' &&
  (window as unknown as { __KEPLER_AUTORUN__?: boolean }).__KEPLER_AUTORUN__ === true;

const savedPref = readPlaybackPref();
const startPlaying =
  savedPref === 'play' || (savedPref === null && (import.meta.env.DEV || autoRunSeam));
if (startPlaying) {
  store.dispatch({ type: 'Play' });
}

// Drive the injected simulation clock from the browser (AD-2). The store's state
// object is replaced each tick, so CanvasHost's existing $effect repaints the
// canvas; no second render trigger is added. The loop advances only while playing,
// so Pause halts motion in every environment.
startAnimationLoop(store);
